/**
 * Cliente HTTP de la API.
 *
 * Resuelve tres cosas que si no se resuelven acá terminan repetidas en cada pantalla: el
 * token de acceso, su renovación automática cuando vence, y la traducción de los Problem
 * Details (RFC 9457) del servidor a un error que la interfaz pueda mostrar tal cual.
 */

/** Base de la API. En desarrollo el proxy de Vite la sirve en el mismo origen. */
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** Error de la API ya traducido: la interfaz muestra `mensaje` sin adornarlo. */
export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
    readonly titulo: string,
    /** Errores por campo, cuando el servidor devuelve un 400 de validación. */
    readonly porCampo: Record<string, string[]> = {},
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  /** El registro no existe, o es de otra clínica: para el usuario es lo mismo. */
  get esNoEncontrado() {
    return this.estado === 404;
  }

  /** Al usuario le falta el permiso. La pantalla debería haberlo ocultado antes. */
  get esPermiso() {
    return this.estado === 403;
  }

  /** El servidor entendió y el negocio dijo que no: se muestra el motivo. */
  get esRegla() {
    return this.estado === 409 || this.estado === 422;
  }
}

type Sesion = {
  tokenAcceso: string;
  refreshToken: string;
  expiraEn: string;
};

let sesion: Sesion | null = null;
let alPerderSesion: (() => void) | null = null;
let renovacionEnCurso: Promise<boolean> | null = null;

const CLAVE = 'advanceodonto.sesion';

/** Recupera la sesión guardada al abrir la aplicación. */
export function restaurarSesion(): Sesion | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    sesion = crudo ? (JSON.parse(crudo) as Sesion) : null;
  } catch {
    // Ventana privada o almacenamiento bloqueado: se trabaja sin recordar la sesión.
    sesion = null;
  }

  return sesion;
}

export function guardarSesion(nueva: Sesion | null) {
  sesion = nueva;

  try {
    if (nueva) {
      localStorage.setItem(CLAVE, JSON.stringify(nueva));
    } else {
      localStorage.removeItem(CLAVE);
    }
  } catch {
    // Sin almacenamiento la sesión vive sólo en memoria; la aplicación sigue funcionando.
  }
}

/** Se avisa a la aplicación cuando la sesión ya no sirve, para volver al ingreso. */
export function alCerrarseLaSesion(accion: () => void) {
  alPerderSesion = accion;
}

export function hayToken() {
  return sesion?.tokenAcceso != null;
}

async function traducirError(respuesta: Response): Promise<ErrorApi> {
  let titulo = 'Error';
  let detalle = `La operación falló (${respuesta.status}).`;
  let porCampo: Record<string, string[]> = {};

  try {
    const cuerpo = await respuesta.json();
    titulo = cuerpo.title ?? titulo;
    detalle = cuerpo.detail ?? detalle;
    porCampo = cuerpo.errors ?? {};

    // En un 400 de validación el detalle genérico no ayuda: manda el primer campo.
    const primero = Object.values(porCampo)[0];
    if (primero?.[0]) {
      detalle = primero[0];
    }
  } catch {
    // Respuesta sin JSON: se queda el mensaje por defecto.
  }

  return new ErrorApi(respuesta.status, detalle, titulo, porCampo);
}

/**
 * Renueva el token con el refresh rotatorio. Se serializa: si tres pantallas piden datos a
 * la vez y el token venció, sólo una renueva y las otras esperan a esa.
 */
async function renovar(): Promise<boolean> {
  if (!sesion?.refreshToken) {
    return false;
  }

  renovacionEnCurso ??= (async () => {
    try {
      const respuesta = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: sesion?.refreshToken }),
      });

      if (!respuesta.ok) {
        return false;
      }

      const cuerpo = await respuesta.json();
      guardarSesion({
        tokenAcceso: cuerpo.tokenAcceso,
        refreshToken: cuerpo.refreshToken,
        expiraEn: cuerpo.expiraEn,
      });

      return true;
    } catch {
      return false;
    } finally {
      renovacionEnCurso = null;
    }
  })();

  return renovacionEnCurso;
}

type Opciones = {
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  /** Para el ingreso y el refresco, que no llevan token. */
  anonimo?: boolean;
  /** Texto plano en lugar de JSON: la historia clínica imprimible. */
  comoTexto?: boolean;
  signal?: AbortSignal;
};

async function ejecutar(ruta: string, opciones: Opciones, reintentando = false): Promise<Response> {
  const cabeceras: Record<string, string> = {};

  if (opciones.cuerpo !== undefined) {
    cabeceras['Content-Type'] = 'application/json';
  }

  if (!opciones.anonimo && sesion?.tokenAcceso) {
    cabeceras.Authorization = `Bearer ${sesion.tokenAcceso}`;
  }

  const respuesta = await fetch(`${BASE}${ruta}`, {
    method: opciones.metodo ?? 'GET',
    headers: cabeceras,
    body: opciones.cuerpo === undefined ? undefined : JSON.stringify(opciones.cuerpo),
    signal: opciones.signal,
  });

  // Un 401 con sesión abierta es token vencido: se renueva una vez y se reintenta.
  if (respuesta.status === 401 && !opciones.anonimo && !reintentando) {
    if (await renovar()) {
      return ejecutar(ruta, opciones, true);
    }

    guardarSesion(null);
    alPerderSesion?.();
  }

  return respuesta;
}

/** Llama a la API y devuelve el cuerpo ya deserializado. Lanza `ErrorApi` si falla. */
export async function api<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const respuesta = await ejecutar(ruta, opciones);

  if (!respuesta.ok) {
    throw await traducirError(respuesta);
  }

  if (respuesta.status === 204) {
    return undefined as T;
  }

  if (opciones.comoTexto) {
    return (await respuesta.text()) as T;
  }

  const texto = await respuesta.text();

  return (texto ? JSON.parse(texto) : undefined) as T;
}

export const obtener = <T,>(ruta: string, signal?: AbortSignal) => api<T>(ruta, { signal });

export const enviar = <T,>(ruta: string, cuerpo: unknown) =>
  api<T>(ruta, { metodo: 'POST', cuerpo });

export const actualizar = <T,>(ruta: string, cuerpo: unknown) =>
  api<T>(ruta, { metodo: 'PUT', cuerpo });

export const borrar = <T,>(ruta: string) => api<T>(ruta, { metodo: 'DELETE' });
